<script setup lang="ts">
/**
 * Компонент для отображения и выбора добавок к продукту
 * Универсальный для desktop и mobile (DRY principle)
 * Следует Vue Style Guide: Priority A
 */

import { computed } from 'vue';
import type { ProductAddon } from '../model/types/product.types';
import { useAppearance } from '~/entities/institution/@x/product';

interface Props {
  addons: ProductAddon[];
  modelValue: number[];
  showLimit?: number;
  isMobile?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: number[]): void;
  (e: 'show-more'): void;
}

const props = withDefaults(defineProps<Props>(), {
  showLimit: 8,
  isMobile: false,
});

const emit = defineEmits<Emits>();

// Локальное значение для v-model
const selectedAddons = computed({
  get: () => props.modelValue,
  set: (value: number[]) => emit('update:modelValue', value),
});

// Переключить аддон
const toggleAddon = (addonId: number) => {
  const index = selectedAddons.value.indexOf(addonId);
  const newValue = [...selectedAddons.value];
  
  if (index > -1) {
    newValue.splice(index, 1);
  } else {
    newValue.push(addonId);
  }
  
  selectedAddons.value = newValue;
};

// Проверка выбран ли аддон
const isSelected = (addonId: number) => selectedAddons.value.includes(addonId);

// Топпинги с картинками или без — настройка заведения в разделе «Внешний вид».
// Без картинок список компактнее; заведениям без фото добавок он и нужен.
const { appearance } = useAppearance();
const withImages = computed(() => appearance.value.productCard.toppingsWithImages);
</script>

<template>
  <div v-if="addons.length" class="product-addons">
    <div class="product-addons__title">
      {{ isMobile ? 'Добавить по вкусу' : 'Дополнения' }}
    </div>

    <!-- Desktop: список с чекбоксами -->
    <ul v-if="!isMobile" class="product-addons__list">
      <li
        v-for="addon in addons"
        :key="addon.id"
        class="product-addons__list-item"
      >
        <img
          v-if="withImages && addon.img && addon.img.url"
          :src="addon.img.url"
          :alt="addon.name"
          class="product-addons__list-image"
        />
        <div class="product-addons__list-text">
          {{ addon.name }} (+{{ addon.price }} ₽)
        </div>
        <UiKitCheckbox
          v-model="selectedAddons"
          :value="addon.id"
          color="success"
          size="lg"
          class="ml-auto"
        />
      </li>
    </ul>

    <!-- Mobile: карточки -->
    <div v-else class="product-addons__grid">
      <div
        v-for="addon in addons"
        :key="addon.id"
        class="product-addons__card"
        :class="isSelected(addon.id) ? 'product-addons__card--selected' : ''"
        @click="toggleAddon(addon.id)"
      >
        <!-- Изображение. Выключено в настройках — блок не рисуем совсем,
             иначе плитка осталась бы с пустым местом под фото. -->
        <div v-if="withImages" class="product-addons__card-image-wrapper">
          <img
            v-if="addon.img && addon.img.url"
            :src="addon.img.url"
            :alt="addon.name"
            class="product-addons__card-image"
          />
          <div v-else class="product-addons__card-placeholder">
            <span class="text-xs text-gray-600">?</span>
          </div>
        </div>

        <!-- Название -->
        <div
          class="product-addons__card-name"
          :class="isSelected(addon.id) ? 'text-gray-800' : 'text-white'"
        >
          {{ addon.name }}
        </div>

        <!-- Цена -->
        <div
          class="product-addons__card-price"
          :class="isSelected(addon.id) ? 'text-gray-600' : 'text-gray-300'"
        >
          +{{ addon.price }} ₽
        </div>

        <!-- Индикатор выбора -->
        <div v-if="isSelected(addon.id)" class="product-addons__card-check">
          <svg class="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
      </div>
    </div>

    <!-- Кнопка "Показать ещё" -->
    <slot name="show-more-button" />
  </div>
</template>

<style scoped lang="scss">
.product-addons {
  // Кегль и зазоры на 1024–1279 на ступень мельче: там правая колонка модалки
  // ~430px, и список добавок с прежними размерами переносился и не помещался
  // в высоту окна. С 1280px всё возвращается к прежнему виду.
  &__title {
    @apply text-sm xl:text-base font-semibold my-2;

    @media (max-width: 768px) {
      @apply text-white;
    }
  }

  // Desktop список
  &__list {
    @apply grid grid-cols-1 gap-3 xl:gap-4;
  }

  &__list-item {
    @apply flex items-center gap-2 xl:gap-3 justify-between;
  }

  &__list-image {
    @apply w-7 h-7 xl:w-8 xl:h-8 object-contain rounded;
  }

  &__list-text {
    @apply font-normal text-xs xl:text-sm;

    @media (max-width: 768px) {
      @apply text-white;
    }
  }

  // Mobile: 4 колонки (эталон в bottom sheet); высота строк задаётся в ProductSettingsSheet
  &__grid {
    @apply grid grid-cols-4 gap-2;
  }

  &__card {
    @apply relative h-full min-h-0 rounded-lg p-2 cursor-pointer transition-all duration-200 grid bg-gray-700/40;

    &--selected {
      @apply bg-white;
    }
  }

  &__card-image-wrapper {
    @apply w-full h-12 mb-2 flex items-center justify-center;
  }

  &__card-image {
    @apply w-full h-auto object-contain rounded;
  }

  &__card-placeholder {
    @apply w-10 h-auto bg-gray-400 rounded flex items-center justify-center;
  }

  &__card-name {
    @apply text-xs font-medium text-center leading-tight mb-1;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  &__card-price {
    @apply text-xs text-center mt-auto;
  }

  &__card-check {
    @apply absolute top-1 right-1 w-4 h-4 bg-hui-success rounded-full flex items-center justify-center;
  }
}
</style>

